import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingCart, Loader2, Minus, Plus, ShieldCheck, ArrowLeft, ArrowRight, Check, Lock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { launchPayment } from "@/lib/payments";
import { timeLeft } from "@/components/DealCountdown";
import { BankTransferPanel } from "@/components/BankTransferPanel";
import { Landmark } from "lucide-react";

const fmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });
const STEPS = ["Quantity", "Review", "Pay"];

const Stepper = ({ step }) => (
  <ol data-testid="buy-stepper" className="flex items-center gap-2">
    {STEPS.map((label, i) => {
      const done = i < step, active = i === step;
      return (
        <li key={label} className="flex items-center gap-2">
          <span data-testid={`buy-step-${i + 1}`} className={`flex h-6 w-6 items-center justify-center border font-mono2 text-[10px] ${done ? "border-emerald-600/60 bg-emerald-600/10 text-emerald-700" : active ? "border-[#6D28D9] bg-[#6D28D9] text-[#FFFFFF]" : "border-slate-300 text-slate-500"}`}>
            {done ? <Check className="h-3 w-3" /> : i + 1}
          </span>
          <span className={`font-mono2 text-[10px] uppercase tracking-[0.2em] ${active ? "text-[#6D28D9]" : "hidden text-slate-500 sm:inline"}`}>{label}</span>
          {i < STEPS.length - 1 && <span className="mx-1 h-px w-4 bg-slate-200 sm:w-6" />}
        </li>
      );
    })}
  </ol>
);

export const BuyShares = ({ company }) => {
  const minLot = Math.max(1, Number(company.lot_size) || 100);
  const [qty, setQty] = useState(minLot);
  const [step, setStep] = useState(0);
  const [authed, setAuthed] = useState(null);
  const [kyc, setKyc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [manual, setManual] = useState(null);
  const [manualBusy, setManualBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me").then(() => {
      setAuthed(true);
      api.get("/kyc").then((r) => setKyc(r.data.status)).catch(() => setKyc("none"));
      const intent = JSON.parse(sessionStorage.getItem("buy_intent") || "null");
      if (intent?.slug === company.slug) {
        sessionStorage.removeItem("buy_intent");
        setQty(Math.max(minLot, Number(intent.qty) || minLot));
        setStep(2);
        toast.success("You're signed in — payment is ready below.");
        setTimeout(() => document.getElementById("buy")?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
      }
    }).catch(() => setAuthed(false));
  }, [company.slug, minLot]);

  const amount = Math.round(company.price * qty * 100) / 100;
  const kycOk = kyc === "submitted" || kyc === "verified";
  const closed = Boolean(timeLeft(company.closes_at)?.closed);
  const startBankTransfer = async () => {
    setManualBusy(true);
    try {
      const { data } = await api.post("/orders/manual", { slug: company.slug, quantity: Number(qty) });
      setManual(data);
      setTimeout(() => document.querySelector('[data-testid="bank-transfer-panel"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    } catch (e) { toast.error(formatApiError(e)); } finally { setManualBusy(false); }
  };

  const primary = "flex items-center gap-2 bg-[#6D28D9] px-7 py-3.5 font-mono2 text-xs font-semibold uppercase tracking-[0.2em] text-[#FFFFFF] transition-colors hover:bg-[#5B21B6] disabled:opacity-50";
  const ghost = "flex items-center gap-2 border border-slate-200 px-5 py-3.5 font-mono2 text-xs uppercase tracking-[0.2em] text-slate-700 transition-colors hover:border-[#6D28D9]/40 hover:text-[#6D28D9]";

  const signIn = () => {
    sessionStorage.setItem("post_auth_redirect", `/company/${company.slug}`);
    sessionStorage.setItem("buy_intent", JSON.stringify({ slug: company.slug, qty }));
    navigate("/client/login");
  };

  const pay = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/orders/checkout", { slug: company.slug, quantity: Number(qty), origin_url: window.location.origin });
      await launchPayment(data, { description: `${qty} × ${company.name}`, onCancel: () => setLoading(false) });
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(false);
    }
  };

  if (closed) {
    return (
      <div data-testid="buy-shares-closed" className="flex flex-wrap items-center justify-between gap-4 border border-slate-200 bg-[#F6F4FD]/70 p-6">
        <div><p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500">Deal window closed</p><p className="mt-2 text-sm text-slate-500">This allocation has closed. Request a quote and the desk will notify you of the next window.</p></div>
        <button data-testid="buy-closed-enquire" onClick={() => { sessionStorage.setItem("enquire_share", company.name); navigate("/"); }} className={ghost}>Request quote</button>
      </div>
    );
  }

  return (
    <div data-testid="buy-shares" className="border border-[#6D28D9]/25 bg-[#F6F4FD]/70 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#6D28D9]/90">Buy in 3 simple steps</p>
        <Stepper step={step} />
      </div>

      {step === 0 && (
        <div className="mt-6 flex flex-wrap items-end gap-6">
          <div>
            <p className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-500">How many shares?</p>
            <div className="mt-2 flex items-center border border-slate-200">
              <button data-testid="buy-qty-minus" type="button" onClick={() => setQty((q) => Math.max(minLot, Number(q) - 100))} className="px-3 py-2.5 text-slate-500 hover:text-[#6D28D9]"><Minus className="h-3.5 w-3.5" /></button>
              <input data-testid="buy-qty-input" type="number" min={minLot} step={100} value={qty} onChange={(e) => setQty(Number(e.target.value) || 0)} onBlur={() => setQty((q) => Math.max(minLot, Number(q) || minLot))} className="w-20 bg-transparent px-2 py-2.5 text-center font-mono2 text-sm text-slate-800 outline-none" />
              <button data-testid="buy-qty-plus" type="button" onClick={() => setQty((q) => Number(q) + 100)} className="px-3 py-2.5 text-slate-500 hover:text-[#6D28D9]"><Plus className="h-3.5 w-3.5" /></button>
            </div>
            <p data-testid="buy-qty-lot" className="mt-2 font-mono2 text-[9px] uppercase tracking-[0.15em] text-slate-500">Minimum lot · {fmt.format(minLot)} shares</p>
          </div>
          <div>
            <p className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-500">Order value</p>
            <p data-testid="buy-amount" className="font-display mt-2 text-2xl font-semibold text-slate-900">₹{fmt.format(amount)}</p>
          </div>
          <button data-testid="buy-next-button" onClick={() => setStep(1)} disabled={qty < minLot} className={primary}>Continue <ArrowRight className="h-3.5 w-3.5" /></button>
          {qty < minLot && <p data-testid="buy-min-hint" className="w-full font-mono2 text-[10px] uppercase tracking-[0.15em] text-rose-600">Minimum order is {fmt.format(minLot)} shares (1 market lot)</p>}
        </div>
      )}

      {step === 1 && (
        <div className="mt-6">
          <dl data-testid="buy-review" className="grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-3">
            {[["Share", company.name], ["Quantity × Price", `${qty} × ₹${fmt.format(company.price)}`], ["Total to pay", `₹${fmt.format(amount)}`]].map(([k, v]) => (
              <div key={k} className="bg-[#F6F4FD] px-5 py-4">
                <dt className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-500">{k}</dt>
                <dd data-testid={k === "Total to pay" ? "buy-review-total" : undefined} className="font-display mt-1 text-lg font-semibold text-slate-900">{v}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-5 flex flex-wrap gap-3">
            <button data-testid="buy-back-button" onClick={() => setStep(0)} className={ghost}><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
            {authed ? (
              <button data-testid="buy-confirm-button" onClick={() => setStep(2)} className={primary}>Confirm order <ArrowRight className="h-3.5 w-3.5" /></button>
            ) : (
              <button data-testid="buy-signin-button" onClick={signIn} disabled={authed === null} className={primary}>Sign in to continue <ArrowRight className="h-3.5 w-3.5" /></button>
            )}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-start gap-3">
            <ShieldCheck className="mt-0.5 h-5 w-5 text-emerald-700" />
            <div>
              <p className="font-display text-lg font-semibold text-slate-900">Pay ₹{fmt.format(amount)} securely</p>
              <p className="mt-1 max-w-md text-sm text-slate-500">Pay online (cards, UPI, net banking) or transfer directly to our HDFC account / UPI QR. Your holding appears in My Account as "Pending confirmation" until the desk completes the off-market transfer.</p>
              {authed && !kycOk && <p data-testid="buy-kyc-nudge" className="mt-2 flex items-center gap-1.5 text-xs text-[#6D28D9]"><Lock className="h-3 w-3" /> No KYC needed to buy — you can complete it later in My Account so the desk can transfer shares to your demat.</p>}
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button data-testid="buy-back-button" onClick={() => { setStep(1); setManual(null); }} disabled={loading} className={ghost}><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
            <button data-testid="buy-bank-button" onClick={startBankTransfer} disabled={loading || manualBusy || !!manual} className={ghost}>
              {manualBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Landmark className="h-3.5 w-3.5" /> Bank transfer / UPI</>}
            </button>
            <button data-testid="buy-button" onClick={pay} disabled={loading} className={primary}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShoppingCart className="h-3.5 w-3.5" /> Pay online</>}
            </button>
          </div>
        </div>
      )}
      {step === 2 && manual && <BankTransferPanel order={manual} />}
    </div>
  );
};
